import type { MouseEvent, PropsWithChildren, ReactNode } from "react"
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"
import DOMPurify from "dompurify"
import {
	IconFolderOutline18 as FolderIcon,
	IconPlusOutline18 as PlusIcon,
} from "nucleo-ui-outline-18"

import { pro } from "@productlane/lib/src/browser"
import {
	IconButton,
	NewRequestIcon,
	NucleoFillIcon,
	Popover,
	Shortcut,
	Tooltip,
} from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { SnippetsProps } from "./snippet-suggestions"
import type { SnippetInput } from "@/screens/settings/settings-pages/snippets-settings-page"
import type { SuggestionProps } from "@tiptap/suggestion"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { useRequiredPlans } from "@/lib/plans"
import { emptyInput } from "@/screens/settings/settings-pages/snippets-settings-page"
import { CreateSnippetDialog } from "./create-snippet-dialog"

const UNCATEGORIZED = "uncategorized"

// undefined = folder list (root), UNCATEGORIZED = snippets without folder
type ActiveFolder = string | undefined

function Highlight({ text, query }: { text: string; query: string }) {
	if (!query) return <>{text}</>

	const lowerText = text.toLowerCase()
	const lowerQuery = query.toLowerCase()
	const parts: ReactNode[] = []
	let cursor = 0
	let matchIndex = lowerText.indexOf(lowerQuery)

	while (matchIndex !== -1) {
		if (matchIndex > cursor) parts.push(text.slice(cursor, matchIndex))
		parts.push(
			<mark
				key={matchIndex}
				className="rounded-[2px] bg-yellow-200/80 text-primary dark:bg-yellow-400/25"
			>
				{text.slice(matchIndex, matchIndex + lowerQuery.length)}
			</mark>,
		)
		cursor = matchIndex + lowerQuery.length
		matchIndex = lowerText.indexOf(lowerQuery, cursor)
	}
	parts.push(text.slice(cursor))

	return <>{parts}</>
}

function ChinHint({ keys, label }: { keys: string[]; label: string }) {
	return (
		<div className="flex items-center gap-0.5">
			<span className="mr-0.5">{label}</span>
			{keys.map((key) => (
				<Shortcut key={key} variant="default">
					{key}
				</Shortcut>
			))}
		</div>
	)
}

export const SnippetsPopover = forwardRef<
	{ setOpen: (open: boolean) => void },
	PropsWithChildren<
		{
			onSelect: (id: string) => void
			initialOpen?: boolean
			anchorRef?: React.RefObject<HTMLElement | null>
		} & Pick<SuggestionProps, "editor"> &
			SnippetsProps
	>
>(
	(
		{
			width,
			children,
			withoutPortal,
			onSelect,
			editor,
			initialOpen = true,
			anchorRef,
		},
		ref,
	) => {
		const [snippets] = useQuery(queries.snippets())
		const [folders] = useQuery(queries.snippetFolders())
		const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)

		const [isPopoverOpen, setPopoverOpen] = useState(initialOpen)
		const [search, setSearch] = useState("")
		const [activeFolderId, setActiveFolderId] = useState<ActiveFolder>()
		const [selectedValue, setSelectedValue] = useState("")

		useImperativeHandle(ref, () => ({
			setOpen: setPopoverOpen,
		}))

		useEffect(() => {
			if (!isPopoverOpen) {
				setSearch("")
				setActiveFolderId(undefined)
			}
		}, [isPopoverOpen])

		useEffect(() => {
			const onKeydown = (event: KeyboardEvent) => {
				if (event.key === "Escape") {
					editor.commands.focus()
				}
			}

			if (isPopoverOpen) {
				document.addEventListener("keydown", onKeydown)
			} else {
				document.removeEventListener("keydown", onKeydown)
			}

			return () => document.removeEventListener("keydown", onKeydown)
		}, [isPopoverOpen, editor])

		// Escape inside a folder navigates back instead of closing. Registered on
		// mount (before Radix's capture listener) so stopImmediatePropagation wins.
		const isOpenRef = useRef(isPopoverOpen)
		isOpenRef.current = isPopoverOpen
		const activeFolderRef = useRef(activeFolderId)
		activeFolderRef.current = activeFolderId

		useEffect(() => {
			const onEscape = (event: KeyboardEvent) => {
				if (!isOpenRef.current) return
				if (event.key !== "Escape") return
				if (activeFolderRef.current === undefined) return

				event.preventDefault()
				event.stopImmediatePropagation()
				setActiveFolderId(undefined)
				setSearch("")
			}

			document.addEventListener("keydown", onEscape, true)
			return () => document.removeEventListener("keydown", onEscape, true)
		}, [])

		const snippetTexts = useMemo(() => {
			const texts = new Map<string, string>()
			const container = document.createElement("div")
			for (const snippet of snippets) {
				container.innerHTML = DOMPurify.sanitize(snippet.html)
				texts.set(
					snippet.id,
					container.textContent?.replace(/\s+/g, " ").trim() ?? "",
				)
			}
			return texts
		}, [snippets])

		const hasFolders = folders.length > 0
		const activeFolder =
			activeFolderId && activeFolderId !== UNCATEGORIZED
				? folders.find((folder) => folder.id === activeFolderId)
				: undefined

		const uncategorizedCount = useMemo(
			() => snippets.filter((snippet) => !snippet.folderId).length,
			[snippets],
		)

		const trimmedSearch = search.trim().toLowerCase()
		const showFolderList = hasFolders && !activeFolderId && !trimmedSearch

		const visibleSnippets = useMemo(() => {
			if (showFolderList) return []

			let scoped = snippets
			if (activeFolderId === UNCATEGORIZED) {
				scoped = snippets.filter((snippet) => !snippet.folderId)
			} else if (activeFolderId) {
				scoped = snippets.filter(
					(snippet) => snippet.folderId === activeFolderId,
				)
			}

			if (!trimmedSearch) return scoped

			return scoped.filter(
				(snippet) =>
					snippet.title.toLowerCase().includes(trimmedSearch) ||
					(snippetTexts.get(snippet.id) ?? "").includes(trimmedSearch),
			)
		}, [showFolderList, snippets, activeFolderId, trimmedSearch, snippetTexts])

		const prefilledInput = useRef<SnippetInput>(emptyInput)

		const [isDialogOpen, setDialogOpen] = useState(false)

		const openEditDialog = useCallback((existingInput: SnippetInput) => {
			prefilledInput.current = existingInput
			setDialogOpen(true)
			analytics.track("edit-snippet")
		}, [])

		const editSnippet = useCallback(
			(existingInput: SnippetInput) => (e: MouseEvent<HTMLButtonElement>) => {
				e.stopPropagation()
				openEditDialog(existingInput)
			},
			[openEditDialog],
		)

		const createSnippet = useCallback(
			(e: MouseEvent<HTMLButtonElement>) => {
				e.stopPropagation()

				prefilledInput.current = {
					...emptyInput,
					folderId:
						activeFolderId && activeFolderId !== UNCATEGORIZED
							? activeFolderId
							: null,
				}
				setDialogOpen(true)
				analytics.track("create-snippet")
			},
			[activeFolderId],
		)

		const closeDialog = useCallback(() => {
			setDialogOpen(false)
			analytics.track("close-snippet-dialog")
		}, [])

		const enterFolder = useCallback((folderId: string) => {
			setActiveFolderId(folderId)
			setSearch("")
			setSelectedValue("")
		}, [])

		const editSelectedSnippet = useCallback(() => {
			const snippet = snippets.find((s) => s.id === selectedValue)
			if (!snippet) return
			openEditDialog({
				id: snippet.id,
				title: snippet.title,
				html: snippet.html,
				folderId: snippet.folderId ?? null,
			})
		}, [snippets, selectedValue, openEditDialog])

		const renderSnippetItem = (snippet: {
			id: string
			title: string
			html: string
			folderId?: string | null
		}) => (
			<CommandItem
				key={`snippet-suggestion-${snippet.id}`}
				value={snippet.id}
				size="lg"
				className="px-2"
				onSelect={() => {
					onSelect(snippet.id)
				}}
			>
				<div className="flex flex-1 items-center gap-3 overflow-hidden">
					<div className="flex min-w-0 flex-1 items-baseline gap-3 overflow-hidden whitespace-nowrap">
						<div className="flex-none font-medium text-primary">
							<Highlight text={snippet.title} query={trimmedSearch} />
						</div>
						<div className="min-w-0 flex-1 truncate font-normal text-tertiary">
							<Highlight
								text={snippetTexts.get(snippet.id) ?? ""}
								query={trimmedSearch}
							/>
						</div>
					</div>
					<div className="invisible flex flex-none group-hover:visible group-aria-selected:visible">
						<IconButton
							Icon={NewRequestIcon}
							variant="ghost"
							size="sm"
							onClick={editSnippet({
								id: snippet.id,
								title: snippet.title,
								html: snippet.html,
								folderId: snippet.folderId ?? null,
							})}
						/>
					</div>
				</div>
			</CommandItem>
		)

		return (
			<>
				<CreateSnippetDialog
					data={prefilledInput.current}
					isOpen={isDialogOpen}
					onOpenChange={closeDialog}
				/>

				<Popover
					{...(anchorRef
						? {
								anchor: (
									<div className="pointer-events-none absolute inset-x-0 top-0 h-px" />
								),
								anchorContainerRef: anchorRef,
							}
						: withoutPortal
							? {}
							: { anchor: <div className="h-4" /> })}
					side={anchorRef ? "top" : undefined}
					align={anchorRef ? "end" : undefined}
					sideOffset={anchorRef ? 8 : undefined}
					onOpenChange={setPopoverOpen}
					open={isPopoverOpen}
					modal={false}
					style={{ width: width ? `${width}px` : undefined }}
					className={`min-w-full p-1 pb-0 [&_[cmdk-input-wrapper]]:px-3`}
					onOpenAutoFocus={(e) => {
						// don't move focus into the popover as this would remove focus from the editor
						e.preventDefault()
					}}
					content={
						!hasRequiredPlan ? (
							<div className="flex flex-col items-start gap-2.5 px-3 py-3 text-sm text-secondary">
								<p>Macros are available on the Pro plan.</p>
								<UpgradeButton required={pro.andUp} />
							</div>
						) : snippets.length === 0 && folders.length === 0 ? (
							<div className="px-3 py-2.5 text-sm text-secondary">
								No snippets yet.{" "}
								<Link
									to="/settings/macros"
									className="font-medium text-primary underline-offset-2 hover:underline"
								>
									Create one
								</Link>
							</div>
						) : (
							<Command
								loop
								shouldFilter={false}
								value={selectedValue}
								onValueChange={setSelectedValue}
								onKeyDown={(e) => {
									if ((e.metaKey || e.ctrlKey) && e.key === "e") {
										e.preventDefault()
										editSelectedSnippet()
									}
								}}
							>
								<CommandInput
									autoFocus
									placeholder={
										activeFolder
											? `Search in ${activeFolder.name}…`
											: activeFolderId === UNCATEGORIZED
												? "Search in Uncategorized…"
												: "Choose macro…"
									}
									value={search}
									onValueChange={setSearch}
								/>
								<div className="absolute right-2 top-2">
									<Tooltip label="Create macro">
										<IconButton
											onClick={createSnippet}
											Icon={PlusIcon}
											variant="ghost"
											className="text-secondary"
										/>
									</Tooltip>
								</div>
								<CommandList className="max-h-80">
									<CommandEmpty className="mx-4 mb-2.5 mt-6 text-sm text-tertiary">
										{showFolderList ? "No folders found" : "No macros found"}
									</CommandEmpty>
									{showFolderList ? (
										<CommandGroup>
											{folders.map((folder) => {
												const count = snippets.filter(
													(snippet) => snippet.folderId === folder.id,
												).length
												return (
													<CommandItem
														key={`snippet-folder-${folder.id}`}
														value={`folder-${folder.id}`}
														size="lg"
														className="px-2"
														onSelect={() => enterFolder(folder.id)}
													>
														<div className="flex flex-1 items-center justify-between gap-3 overflow-hidden">
															<span className="flex min-w-0 items-center gap-3">
																{folder.icon ? (
																	<NucleoFillIcon
																		icon={folder.icon}
																		className="h-4 w-4 flex-none text-secondary group-aria-selected:text-primary"
																	/>
																) : (
																	<FolderIcon className="h-4 w-4 flex-none text-secondary group-aria-selected:text-primary" />
																)}
																<span className="truncate font-medium text-primary">
																	{folder.name}
																</span>
															</span>
															<span className="flex-none text-xs font-normal text-tertiary">
																{count}
															</span>
														</div>
													</CommandItem>
												)
											})}
											{uncategorizedCount > 0 && (
												<CommandItem
													value={`folder-${UNCATEGORIZED}`}
													size="lg"
													className="px-2"
													Icon={FolderIcon}
													onSelect={() => enterFolder(UNCATEGORIZED)}
												>
													<div className="flex flex-1 items-center justify-between gap-3 overflow-hidden">
														<span className="truncate font-medium text-primary">
															Uncategorized
														</span>
														<span className="flex-none text-xs font-normal text-tertiary">
															{uncategorizedCount}
														</span>
													</div>
												</CommandItem>
											)}
										</CommandGroup>
									) : (
										<CommandGroup>
											{visibleSnippets.map(renderSnippetItem)}
										</CommandGroup>
									)}
								</CommandList>
								<div className="mt-1 flex items-center justify-between border-t border-gray-200 px-3 py-2 text-xs text-tertiary dark:border-gray-700">
									<div className="flex items-center gap-3">
										<ChinHint keys={["↑", "↓"]} label="Navigate" />

										<ChinHint
											keys={["esc"]}
											label={activeFolderId ? "Go back" : "Close"}
										/>
										{!showFolderList && (
											<ChinHint keys={["⌘", "E"]} label="Edit" />
										)}
									</div>

									<ChinHint
										keys={["↵"]}
										label={showFolderList ? "Open" : "Select"}
									/>
								</div>
							</Command>
						)
					}
				>
					{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
					<div onClick={() => setPopoverOpen(!isPopoverOpen)}>
						<Tooltip label="Choose macro">{children}</Tooltip>
					</div>
				</Popover>
			</>
		)
	},
)
